/**
 * Channel view component.
 * @module components/View/ChannelView
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Confirm } from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { deleteContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';

/**
 * Channel view component class.
 * @function ChannelView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ChannelView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push(parentUrl);
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">Channel</div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete channel"
        content={`Are you sure you want to delete "${content.title}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <div className="view-padding">
        <article id="channel">
          <h1 className="content-title">{content.title}</h1>
          {content.description && (
            <p className="description">{content.description}</p>
          )}
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ChannelView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
};

export default ChannelView;
